"use client";

import { c as _c } from "react/compiler-runtime";
import { motion, useReducedMotion } from "framer-motion";
import { QrCode, IndianRupee, Check, Minus, Plus, Clock } from "lucide-react";
import { Card, CardContent } from "@/shared/components/ui/card";
import { type ShowcaseBlock } from "../data/features";
const MembersMock = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center gap-3"><span className="flex size-11 items-center justify-center rounded-full bg-primary/15 text-sm font-bold text-primary">AS</span><div><p className="text-sm font-bold">Arjun Sharma</p><p className="text-xs text-muted-foreground">Member since Jan 2025</p></div><span className="ml-auto rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary">Gold · 12 mo</span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex justify-between"><span className="text-muted-foreground">Trainer</span><span className="font-medium">Coach Ravi</span></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mt-4 space-y-2 border-t border-border/60 pt-4 text-sm">{t1}<div className="flex justify-between"><span className="text-muted-foreground">Fee</span><span className="font-medium">₹24,000 / yr</span></div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Card className="gap-0 py-0 shadow-lg"><CardContent className="p-5">{t0}{t2}<div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-600"><Clock className="size-3.5" /> Renews in 9 days</div></CardContent></Card>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};

// A faux PDF invoice with a QR block — sells the billing story.
const BillingMock = () => {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div><p className="text-xs font-medium text-muted-foreground">INVOICE</p><p className="text-lg font-bold">#INV-2048</p></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-start justify-between">{t0}<span className="flex size-16 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground"><QrCode className="size-10" /></span></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex justify-between"><span className="text-muted-foreground">Gold plan · 12 months</span><span className="font-medium">₹24,000</span></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex justify-between"><span className="text-muted-foreground">Festive offer · 10%</span><span className="font-medium text-emerald-600">−₹2,400</span></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mt-4 space-y-2 border-t border-border/60 pt-4 text-sm">{t2}{t3}<div className="flex justify-between border-t border-border/60 pt-2 text-base font-bold"><span>Paid</span><span>₹21,600</span></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Card className="gap-0 py-0 shadow-lg"><CardContent className="p-5">{t1}{t4}<div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary"><Check className="size-3.5" /> Receipt emailed</div></CardContent></Card>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};

// A mini P&L card with revenue/expense lines and a sparkline of profit.
const FinancialsMock = () => {
  const $ = _c(8);
  const reduce = useReducedMotion();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [30, 45, 38, 60, 55, 72, 68, 85];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const points = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="text-sm font-semibold">{"June P&L"}</p>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mt-3 grid grid-cols-3 gap-2 text-center">{[{
        l: "Income",
        v: "\u20B96.1L",
        c: "text-foreground"
      }, {
        l: "Expenses",
        v: "\u20B93.4L",
        c: "text-foreground"
      }, {
        l: "Net profit",
        v: "\u20B92.7L",
        c: "text-emerald-600"
      }].map(_temp)}</div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== reduce) {
    t3 = <div className="mt-4 flex h-20 items-end gap-1.5">{points.map((h, i) => <motion.div key={i} className="flex-1 rounded-t bg-gradient-to-t from-primary/30 to-primary" initial={reduce ? false : {
        height: 0
      }} whileInView={{
        height: `${h}%`
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: i * 0.05
      }} />)}</div>;
    $[3] = reduce;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <p className="mt-2 text-[11px] text-muted-foreground">+18% net margin vs last month</p>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3) {
    t5 = <Card className="gap-0 py-0 shadow-lg"><CardContent className="p-5">{t1}{t2}{t3}{t4}</CardContent></Card>;
    $[6] = t3;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};

// A compact attendance calendar with present/absent chips.
const AttendanceMock = () => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [1, 1, 2, 1, 1, 0, 0, 1, 1, 1, 2, 1, 1, 0, 1, 1, 1, 1, 2, 1, 0, 1, 1, 3];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const days = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="text-sm font-semibold">Attendance · June</p>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="mt-3 grid grid-cols-8 gap-1.5">{days.map(_temp2)}</div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-primary/40" />{" "}Present</span>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Card className="gap-0 py-0 shadow-lg"><CardContent className="p-5">{t1}{t2}<div className="mt-4 flex gap-4 text-[11px] text-muted-foreground">{t3}<span className="flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-destructive/40" />{" "}Absent</span></div></CardContent></Card>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};

// A payslip-style breakdown ending in net payable.
const PayrollMock = () => {
  const $ = _c(4);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center justify-between"><p className="text-sm font-semibold">Payslip · Coach Ravi</p><span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary">June</span></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [["Base salary", "\u20B930,000"], ["Per-day rate", "\u20B91,000"], ["Leave (2 days)", "\u2212\u20B92,000"]].map(_temp3);
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span>Net payable</span>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Card className="gap-0 py-0 shadow-lg"><CardContent className="p-5">{t0}<div className="mt-4 space-y-2 text-sm">{t1}<div className="flex justify-between border-t border-border/60 pt-2 text-base font-bold">{t2}<span className="inline-flex items-center text-emerald-600"><IndianRupee className="size-4" />28,000</span></div></div></CardContent></Card>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};

// A POS cart with line items and a running total.
const PosMock = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className="text-sm font-semibold">Point of sale</p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mt-3 space-y-2">{[{
        n: "Whey protein 1kg",
        q: 1,
        p: "\u20B92,200"
      }, {
        n: "Shaker bottle",
        q: 2,
        p: "\u20B9600"
      }].map(_temp4)}</div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Card className="gap-0 py-0 shadow-lg"><CardContent className="p-5">{t0}{t1}<div className="mt-3 flex justify-between border-t border-border/60 pt-3 text-base font-bold"><span>Total</span><span>₹2,800</span></div></CardContent></Card>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

// Pick the right mock for a showcase block by its id.
export const ShowcaseMock = t0 => {
  const $ = _c(6);
  const {
    id
  } = t0;
  if (id === "members") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <MembersMock />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (id === "billing") {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <BillingMock />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (id === "financials") {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FinancialsMock />;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (id === "attendance") {
    let t1;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <AttendanceMock />;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  if (id === "payroll") {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <PayrollMock />;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PosMock />;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
function _temp(m) {
  return <div key={m.l} className="rounded-lg border border-border/60 bg-background/60 p-2.5"><p className={`text-sm font-bold ${m.c}`}>{m.v}</p><p className="text-[11px] text-muted-foreground">{m.l}</p></div>;
}
function _temp2(d, i) {
  return <div key={i} className={`flex aspect-square items-center justify-center rounded-md text-[10px] font-semibold ${d === 1 ? "bg-primary/15 text-primary" : d === 2 ? "bg-destructive/10 text-destructive" : d === 3 ? "bg-primary text-primary-foreground ring-2 ring-primary/40" : "bg-background/60 text-muted-foreground/40"}`}>{i + 1}</div>;
}
function _temp3(t0) {
  const [l, v] = t0;
  return <div key={l} className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="font-medium">{v}</span></div>;
}
function _temp4(it) {
  return <div key={it.n} className="flex items-center justify-between rounded-lg border border-border/60 bg-background/60 px-3 py-2"><span className="text-sm font-medium">{it.n}</span><div className="flex items-center gap-2"><span className="flex items-center gap-1 rounded-md border border-border/70 px-1.5 py-0.5 text-xs"><Minus className="size-3" /> {it.q}{" "}<Plus className="size-3" /></span><span className="w-14 text-right text-sm font-semibold">{it.p}</span></div></div>;
}